import { Box, MenuItem, Pagination, Select, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { PaginationMeta } from './tableTemplate/TableTemplateTypes';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

interface TablePaginationBarProps {
  meta: PaginationMeta;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

/**
 * Pagination bar: rows-per-page selector + displayed-rows count on the left,
 * numbered page buttons (1, 2, 3 … N) with first/previous/next/last arrows on the
 * right. Page numbers are 1-based end to end (matches the API `meta.page`).
 */
export function TablePaginationBar({
  meta,
  page,
  limit,
  onPageChange,
  onLimitChange,
}: TablePaginationBarProps) {
  const { t } = useTranslation('common');

  // "1-25 sur 60" — same computation MUI's TablePagination used, kept for parity.
  const from = meta.total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, meta.total);
  // MUI Pagination needs count >= 1 even when there are no pages.
  const pageCount = Math.max(1, meta.totalPages);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 2,
        px: 2,
        py: 1,
      }}
    >
      {/* Left: rows-per-page selector + displayed-rows count */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.label' }}>
          {t('pagination.rowsPerPage')}
        </Typography>
        <Select
          size="small"
          variant="standard"
          value={limit}
          // Accessible name: the visible label sits in an adjacent Typography, so
          // the select itself needs an explicit name (MUI's TablePagination used to
          // wire this up; our custom bar must do it). Screen readers would otherwise
          // announce a bare "combobox".
          aria-label={t('pagination.rowsPerPage')}
          onChange={e => {
            onLimitChange(Number(e.target.value));
            onPageChange(1);
          }}
          disableUnderline
          sx={{
            color: 'primary.main',
            // Match the pagination number / labels size (body2, 0.875rem).
            fontSize: '0.875rem',
            lineHeight: 1.43,
            // Kill the standard-input asymmetric vertical padding so "25" sits on
            // the same baseline/line as the surrounding labels & page numbers.
            '& .MuiSelect-select': {
              fontSize: '0.875rem',
              lineHeight: 1.43,
              paddingTop: 0,
              paddingBottom: 0,
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiSelect-icon': { color: 'primary.main' },
            // No standard-variant underline under the value (e.g. "25").
            '&:before, &:after': { display: 'none' },
          }}
          MenuProps={{
            sx: theme => ({
              '& .MuiMenuItem-root': { color: 'primary.main' },
              '& .MuiMenuItem-root.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              '& .MuiMenuItem-root:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              },
            }),
          }}
        >
          {ROWS_PER_PAGE_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="body2" sx={{ color: 'primary.main' }}>
          {t('pagination.displayedRows', { from, to, count: meta.total })}
        </Typography>
      </Box>

      {/* Right: numbered page buttons + first/prev/next/last arrows.
          Blue text on a white background; the current page is highlighted with a
          faint blue tint (kept blue-on-white, not a solid blue fill). */}
      <Pagination
        // MUI Pagination expects count >= 1; totalPages can be 0 (empty result).
        // Clamp so page/count stay consistent even if a caller renders the bar
        // with no rows.
        count={pageCount}
        page={Math.min(page, pageCount)}
        onChange={(_, newPage) => onPageChange(newPage)}
        showFirstButton
        showLastButton
        shape="rounded"
        siblingCount={1}
        boundaryCount={1}
        sx={{
          '& .MuiPaginationItem-root': {
            color: 'primary.main',
            backgroundColor: 'common.white',
          },
          '& .MuiPaginationItem-root.Mui-selected': {
            color: 'primary.main',
            fontWeight: theme => theme.typography.fontWeightBold,
            backgroundColor: theme => alpha(theme.palette.primary.main, 0.12),
            '&:hover': {
              backgroundColor: theme => alpha(theme.palette.primary.main, 0.18),
            },
          },
          '& .MuiPaginationItem-root:hover': {
            backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
          },
          '& .MuiPaginationItem-root.Mui-disabled': {
            color: 'action.disabled',
          },
        }}
      />
    </Box>
  );
}
